import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { api } from "../lib/api";

// India TopoJSON served from our own public folder (states + districts)
const INDIA_TOPO_JSON = "/india-topo.json";

export default function IndiaMap({ selected, onSelect, className = "" }) {
    const [regions, setRegions] = useState([]);

    useEffect(() => {
        api.get("/regions").then((r) => setRegions(r.data));
    }, []);

    const active = useMemo(() => regions.find((r) => r.id === selected), [regions, selected]);

    return (
        <div className={`relative w-full ${className}`}>
            <div className="relative w-full max-w-2xl mx-auto grain-card rounded-md overflow-hidden bg-[#0F0C09]">
                <ComposableMap
                    data-testid="india-map-svg"
                    projection="geoMercator"
                    projectionConfig={{ scale: 900, center: [82, 22] }}
                    width={520}
                    height={620}
                    style={{ width: "100%", height: "auto" }}
                >
                    <Geographies geography={INDIA_TOPO_JSON} parseGeographies={(geos) => geos}>
                        {({ geographies }) =>
                            geographies.map((geo) => (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    style={{
                                        default: { fill: "#1A1512", stroke: "rgba(255,107,26,0.35)", strokeWidth: 0.5, outline: "none" },
                                        hover: { fill: "#2A1F17", stroke: "rgba(255,107,26,0.55)", strokeWidth: 0.6, outline: "none" },
                                        pressed: { fill: "#2A1F17", outline: "none" },
                                    }}
                                />
                            ))
                        }
                    </Geographies>
                    {regions.map((r) => {
                        const isActive = selected === r.id;
                        const rad = isActive ? 7 : 4.5;
                        return (
                            <Marker
                                key={r.id}
                                coordinates={[r.lng, r.lat]}
                                onClick={() => onSelect(isActive ? "" : r.id)}
                                style={{ cursor: "pointer" }}
                            >
                                <g data-testid={`map-marker-${r.id}`}>
                                    {isActive && (
                                        <circle r={rad + 6} fill="#FF6B1A" opacity={0.2} />
                                    )}
                                    <circle
                                        r={rad}
                                        fill={isActive ? "#FF6B1A" : "#FFB800"}
                                        stroke="#0A0906"
                                        strokeWidth={1.5}
                                    />
                                    {isActive && (
                                        <text
                                            textAnchor="middle"
                                            y={-14}
                                            style={{
                                                fontFamily: "'JetBrains Mono', monospace",
                                                fontSize: 9,
                                                letterSpacing: "0.1em",
                                                textTransform: "uppercase",
                                                fill: "#FF6B1A",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            {r.name}
                                        </text>
                                    )}
                                </g>
                            </Marker>
                        );
                    })}
                </ComposableMap>
            </div>

            {active && (
                <div className="mt-6 grain-card p-6 rounded-md max-w-2xl mx-auto" data-testid="map-active-region">
                    <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-1">/ {active.state}</p>
                    <h3 className="font-display font-extrabold text-3xl tracking-tighter uppercase">{active.name}</h3>
                    <p className="text-brand-marigold text-sm font-mono-tag uppercase mt-1">{active.craft}</p>
                    <p className="text-white/60 text-sm mt-3">{active.startup_count} brand{active.startup_count === 1 ? "" : "s"} listed from here.</p>
                    <Link to={`/discover?region=${active.id}`} data-testid="map-explore-region-btn"
                        className="mt-4 inline-block font-mono-tag text-xs uppercase text-brand-saffron border-b border-brand-saffron hover:text-brand-marigold hover:border-brand-marigold transition-colors">
                        Explore brands →
                    </Link>
                </div>
            )}

            {/* Legend */}
            <div className="mt-4 flex items-center gap-4 justify-center text-white/50 font-mono-tag text-[10px] uppercase">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-brand-marigold" /> heritage cluster</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-brand-saffron" /> selected</span>
            </div>
        </div>
    );
}
